/* eslint-disable no-param-reassign */
import { Injectable, inject } from '@angular/core';

import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { CalendarPage, IntervalCount, IntervalFilter, IntervalLink, SportIntervalCounts } from '@frontend/sports/calendar/data-access';
import { CalendarConfig, CalendarData, PrettyUrlsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { CalendarUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { SportsTimeFilterRestriction } from '@frontend/sports/types/components/calendar';
import { IntlService, LoadingIndicatorService } from '@frontend/vanilla/core';
import { findLast, orderBy, sumBy } from 'lodash-es';
import { Observable, map } from 'rxjs';

import { CalendarIntervalService } from './calendar-interval.service';
import { CountIntervalService } from './count-interval.service';

@Injectable({ providedIn: 'root' })
export class CalendarService {
    private bettingService = inject(BettingOfferApi);
    private countIntervalService = inject(CountIntervalService);
    private indicatorService = inject(LoadingIndicatorService);
    private intlService = inject(IntlService);
    private calendarIntervalService = inject(CalendarIntervalService);
    private urlConfig = inject(PrettyUrlsConfig);
    private urlHelper = inject(UrlHelperService);
    sitecore = inject(Sitecore);
    private calendarData = inject(CalendarData);
    private calendarConfig = inject(CalendarConfig);

    getInterval(name?: string): IntervalFilter | undefined {
        const intervalValues = Object.values(this.getCalendarIntervals());

        return findLast(intervalValues, (current) => current.name === name);
    }

    isInterval(name?: string): boolean {
        if (!name) {
            return false;
        }

        const { translations } = this.urlConfig;

        return [
            translations.today,
            translations.tomorrow,
            translations.after2days,
            translations.after3days,
            translations.in30minutes,
            translations.in60minutes,
            translations.in180minutes,
            translations.next2days,
            translations.next3days,
            translations.next5days,
            translations.midWeek,
            translations.thisWeekend,
        ].includes(name);
    }

    getTimeInterval(name?: string): { from?: string; to?: string } | undefined {
        const current = this.getInterval(name);

        if (current) {
            return {
                from: current.from?.toISOString(),
                to: current.to?.toISOString(),
            };
        }

        return;
    }

    isEnhancedAccasHidden(sportId: number): boolean {
        return this.calendarConfig.hideEnhancedAccas ? this.calendarConfig.hideEnhancedAccas.includes(sportId) : false;
    }

    /**
     * Returns a date that is relative to the current date by the number of daysFromCurrentDate. It is formatted with a given format or 'shortDate'.
     *
     * @param [daysFromCurrentDate=0]
     * @param [format]
     * @returns
     *
     * @memberOf Calendar
     */
    getDate(daysFromCurrentDate = 0, format?: string): string {
        let date = DateProviderService.getDate();
        format = format || 'shortDate';

        if (daysFromCurrentDate > 0) {
            date = new Date(date.setDate(date.getDate() + daysFromCurrentDate));
        }

        return this.intlService.formatDate(date, format) || '';
    }

    getCalendarCounts(sportId?: string): Observable<SportIntervalCounts> {
        const indicator = this.indicatorService.start();
        try {
            const intervals = this.getCalendarIntervals();

            return this.countIntervalService.getCountsForSport(intervals, sportId);
        } finally {
            indicator.done();
        }
    }

    isCalendarAvailable(sportId: string): Observable<boolean> {
        const intervals = this.getCalendarIntervals();
        const params = this.countIntervalService.buildRequestParameters(
            {
                calendar: {
                    from: intervals.today.from,
                    to: intervals.after3days.to,
                    title: 'calendar',
                    name: 'calendar',
                    key: 'calendar',
                },
            },
            sportId,
        );

        return this.bettingService.getBatchCounts(params).pipe(
            map((result) => {
                const tags = result ? result.calendar : [];

                return !!sumBy(tags, (tag) => tag.preMatch + tag.live);
            }),
        );
    }

    getCalendarIntervals(): Record<string, IntervalFilter> {
        return this.calendarIntervalService.getCalendarIntervals();
    }

    getCalendarLinks(pageName: CalendarPage, sport?: UrlParam, activeLink?: string): IntervalLink[] {
        const time = Date.now();
        const intervals = this.getCalendarIntervals();
        const intervalsPage = this.getSitecoreIntervals(intervals, pageName, sport);
        const links = intervalsPage.map((key) => this.getUrl(key, intervals[key], sport, activeLink)).filter(isDefined);

        return orderBy(links, [(link) => (!link.to || link.to.getTime() < time ? time : link.to)], ['asc']);
    }

    private getSitecoreIntervals(
        intervals: {
            [key: string]: IntervalFilter;
        },
        pageName: CalendarPage,
        sport?: UrlParam,
    ): string[] {
        switch (pageName) {
            case CalendarPage.Featured:
                return this.getPageIntervals(intervals, this.calendarData.calendarItems.featurePage, sport);

            case CalendarPage.Competitions:
                return this.getPageIntervals(intervals, this.calendarData.calendarItems.competitionPage, sport);

            case CalendarPage.AtozSports:
                return this.getIntervalForAllSports(this.calendarData.calendarItems.atoZPage);
            default:
                return [];
        }
    }

    private getIntervalForAllSports(intervals: string[]): string[] {
        const keyList: string[] = [];

        intervals.forEach((detail) => {
            keyList.push(detail);
        });

        return keyList;
    }

    private getPageIntervals(
        intervals: {
            [key: string]: IntervalFilter;
        },
        sportDetails: SportsTimeFilterRestriction[],
        sport?: UrlParam,
    ): string[] {
        const keyList: string[] = [];

        sportDetails.forEach((detail) => {
            let intervalKey: string | undefined = '';
            if (detail.allSports) {
                intervalKey = intervals[detail.timeFilter] ? detail.timeFilter : undefined;
            } else if (detail.sports.find((k) => k.name?.toLowerCase() === sport?.name.toLowerCase())) {
                intervalKey = intervals[detail.timeFilter] ? detail.timeFilter : undefined;
            }
            if (intervalKey) {
                keyList.push(intervalKey);
            }
        });

        return keyList;
    }

    getUrl(
        key: string,
        interval: IntervalFilter | IntervalCount,
        sport: UrlParam | undefined,
        activeLink?: string,
        region?: UrlParam,
        league?: UrlParam,
    ): IntervalLink | undefined {
        let url: string;

        if (key === 'live') {
            return;
        } else {
            if (sport) {
                url = region ? this.urlHelper.getSportUrl(sport, interval.name, region, league) : this.urlHelper.getSportUrl(sport, interval.name);
            } else {
                url = this.urlHelper.getCalendarUrl(interval.name);
            }

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            interval.date = this.intlService.formatDate(interval.from!, this.calendarConfig.topMenuDateFormat) || '';
            interval.title = this.getIntervalTitle(key, interval);
        }

        interval.icon = this.sitecore.globalMessages.SportsCalendarIcon;
        interval.active = interval.name === activeLink;

        return { ...interval, url };
    }

    getIntervalTitle(key: string, interval: IntervalFilter): string {
        switch (key) {
            case CalendarUrlParam.In30minutes:
                return this.sitecore.globalMessages.in30Minutes;
            case CalendarUrlParam.In60minutes:
                return this.sitecore.globalMessages.in60Minutes;
            case CalendarUrlParam.In180minutes:
                return this.sitecore.globalMessages.in180Minutes;
            case CalendarUrlParam.After2days:
                return this.sitecore.globalMessages.After2Days;
            case CalendarUrlParam.After3days:
                return this.sitecore.globalMessages.After3Days;
            default:
                return interval.title;
        }
    }
}
